import React, { useMemo, useState, useCallback } from 'react';
import { connect, useDispatch, useStore } from 'react-redux';
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaLayerGroup,
  FaDoorOpen,
  FaPlus,
  FaMinus,
  FaTrash,
  FaPlusCircle,
  FaSearch,
  FaTimes
} from 'react-icons/fa';
import { SortableTree, SimpleTreeItemWrapper } from 'dnd-kit-sortable-tree';
import {
  selectObjectTree,
  setSelectedObject,
  addObject,
  setObjects,
  OBJECT_TYPES
} from '../../../actions/infoTerminal';
import { canAddFloor, addFloorWithValidation, deleteObject, saveAllChanges, discardChanges } from '../../../actions/infoTerminalApi';
import { createNotification } from '../../notifications/notifications';
import styles from './styles.module.scss';
import ConfirmModal from '../Delete';
import BuildingSelectModal from '../BuildingSelectModal';
import RoomSelectModal from '../RoomSelectModal';

const ICONS = {
  territory: FaMapMarkerAlt,
  building: FaBuilding,
  floor: FaLayerGroup,
  room: FaDoorOpen
};

const ObjectTree = ({
  objectTree,
  objects,
  selectedObjectId,
  setSelectedObject,
  addObject,
  deleteObject,
  setObjects
}) => {
  const dispatch = useDispatch();

  const [collapsedIds, setCollapsedIds] = useState(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Состояние для модалок выбора из справочников
  const [buildingModalOpen, setBuildingModalOpen] = useState(false);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState(null);

  // Состояние для модалки предупреждения о несохранённых изменениях
  const [unsavedChangesConfirm, setUnsavedChangesConfirm] = useState(null);

  // Фильтрация дерева по поисковому запросу
  const filterTree = useCallback((nodes, term) => {
    if (!term.trim()) return nodes;

    const lowerTerm = term.toLowerCase();

    const filterNodes = (nodeList) => {
      return nodeList.reduce((acc, node) => {
        const nameMatch = node.name?.toLowerCase().includes(lowerTerm);
        const filteredChildren = node.children ? filterNodes(node.children) : [];

        // Включаем узел если он сам совпадает или имеет совпадающих потомков
        if (nameMatch || filteredChildren.length > 0) {
          acc.push({
            ...node,
            children: filteredChildren,
            // При поиске раскрываем все узлы с совпадениями
            collapsed: false
          });
        }
        return acc;
      }, []);
    };

    return filterNodes(nodes);
  }, []);
  
    const confirmDelete = () => {
      if (deleteConfirm) {
        deleteObject(deleteConfirm.id);
        setDeleteConfirm(null);
      }
    };
  
    const handleDelete = (item, e) => {
      e.stopPropagation();
      setDeleteConfirm(item);
    };

  // Преобразуем дерево в формат для SortableTree с фильтрацией
  const treeItems = useMemo(() => {
    const addCollapsedState = (nodes) => {
      return nodes.map(node => ({
        ...node,
        collapsed: searchTerm ? false : collapsedIds.has(node.id),
        children: node.children ? addCollapsedState(node.children) : []
      }));
    };
    const withCollapsed = addCollapsedState(objectTree);
    return filterTree(withCollapsed, searchTerm);
  }, [objectTree, collapsedIds, searchTerm, filterTree]);

  const handleToggleCollapse = (id) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Используем useStore для получения актуального состояния СИНХРОННО в момент клика
  // (useSelector и props из connect используют батчинг React и могут быть устаревшими)
  const store = useStore();

  const handleSelect = (id) => {
    // Получаем актуальное состояние напрямую из store в момент клика
    const state = store.getState();
    const { hasChanges, modifiedObjectIds, pendingAreaUpdates } = state.infoTerminal;

    // Проверяем наличие несохранённых изменений
    const hasObjectChanges = modifiedObjectIds instanceof Set
      ? modifiedObjectIds.size > 0
      : (modifiedObjectIds && Object.keys(modifiedObjectIds).length > 0);
    const hasAreaChanges = pendingAreaUpdates && Object.keys(pendingAreaUpdates).length > 0;
    const hasAnyChanges = hasChanges || hasObjectChanges || hasAreaChanges;

    if (hasAnyChanges && selectedObjectId !== id) {
      setUnsavedChangesConfirm({ targetId: id });
      return;
    }
    setSelectedObject(id);
  };

  const handleSaveAndSwitch = async () => {
    if (unsavedChangesConfirm) {
      await dispatch(saveAllChanges());
      setSelectedObject(unsavedChangesConfirm.targetId);
      setUnsavedChangesConfirm(null);
    }
  };

  const handleDiscardAndSwitch = async () => {
    if (unsavedChangesConfirm) {
      await dispatch(discardChanges());
      setSelectedObject(unsavedChangesConfirm.targetId);
      setUnsavedChangesConfirm(null);
    }
  };

  const handleAddChild = (parentId, e) => {
    e.stopPropagation();

    const parent = objects.find(obj => obj.id === parentId);

    if (parent?.type === 'territory') {
      // Открыть модалку выбора здания из справочника УМБ
      setSelectedParentId(parentId);
      setBuildingModalOpen(true);
    } else if (parent?.type === 'building') {
      // Валидация этажности + добавление этажа
      const validation = dispatch(canAddFloor(parentId));
      if (!validation.valid) {
        createNotification('warning', validation.message);
        return;
      }
      // Добавление этажа через API
      dispatch(addFloorWithValidation(parentId));
    } else if (parent?.type === 'floor') {
      // Открыть модалку выбора аудитории из справочника
      setSelectedParentId(parentId);
      setRoomModalOpen(true);
    } else {
      // Для остальных случаев (или если нет parent) - старая логика
      addObject(parentId);
    }

    // Раскрываем родителя
    setCollapsedIds(prev => {
      const next = new Set(prev);
      next.delete(parentId);
      return next;
    });
  };

  // Обработка изменения дерева (drag-drop)
  const handleItemsChanged = (newItems) => {
    // Преобразуем дерево обратно в плоский массив с обновленными parentId и sortOrder
    const flattenTree = (nodes, parentId = null) => {
      const result = [];
      nodes.forEach((node, index) => {
        const { children, collapsed, ...rest } = node;
        result.push({
          ...rest,
          parentId,
          sortOrder: index + 1
        });
        if (children && children.length > 0) {
          result.push(...flattenTree(children, node.id));
        }
      });
      return result;
    };

    const updatedObjects = flattenTree(newItems);
    setObjects(updatedObjects);
  };

  const TreeRow = useMemo(
    () =>
      React.forwardRef((props, ref) => {
        const {
          item,
          onCollapse,
          className,
          contentClassName,
          isDragging
        } = props;

        const hasChildren = item.children && item.children.length > 0;
        const collapsed = item.collapsed;
        const isSelected = selectedObjectId === item.id;
        const Icon = ICONS[item.type] || FaMapMarkerAlt;
        const canAddChild = OBJECT_TYPES[item.type]?.allowedChildren?.length > 0;

        return (
          <SimpleTreeItemWrapper
            {...props}
            ref={ref}
            className={`${styles.treeItem} ${className || ''}`}
            contentClassName={`${styles.itemContent} ${contentClassName || ''}`}
            manualDrag
            showDragHandle={false}
            disableCollapseOnItemClick
            indentationWidth={10}
            style={{
              paddingLeft: `0px`
            }}
          >
            <div
              className={`${styles.row} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''} ${!item.isActive ? styles.inactive : ''}`}
              onClick={() => handleSelect(item.id)}
            >
              <button
                type="button"
                className={styles.toggleButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren && typeof onCollapse === 'function') {
                    handleToggleCollapse(item.id);
                    onCollapse(e);
                  }
                }}
                disabled={!hasChildren}
              >
                {hasChildren && (collapsed ? <FaPlus size={10} /> : <FaMinus size={10} />)}
              </button>

              <span className={styles.nodeIcon}>
                <Icon size={14} />
              </span>

              <span className={styles.nodeTitle} title={item.name}>
                {item.name}
              </span>

              <div className={styles.actions}>
                {canAddChild && (
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={(e) => handleAddChild(item.id, e)}
                    title={`Добавить ${OBJECT_TYPES[item.type].allowedChildren[0] === 'building' ? 'здание' :
                            OBJECT_TYPES[item.type].allowedChildren[0] === 'floor' ? 'этаж' : 'аудиторию'}`}
                  >
                    <FaPlusCircle size={12} />
                  </button>
                )}

                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  onClick={(e) => handleDelete(item, e)}
                  title="Удалить"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            </div>
          </SimpleTreeItemWrapper>
        );
      }),
    [selectedObjectId, collapsedIds]
  );

  return (
    <div className={styles.treeContainer}>
      {/* Поле поиска */}
      <div className={styles.searchBox}>
        <FaSearch className={styles.searchIcon} size={12} />
        <input
          type="text"
          placeholder="Поиск по объектам..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        {searchTerm && (
          <button
            className={styles.clearSearch}
            onClick={() => setSearchTerm('')}
            title="Очистить"
          >
            <FaTimes size={12} />
          </button>
        )}
      </div>

      <SortableTree
        items={treeItems}
        onItemsChanged={handleItemsChanged}
        TreeItemComponent={TreeRow}
        indentationWidth={20}
        pointerSensorOptions={{ activationConstraint: { distance: 5 } }}
      />
      <ConfirmModal
          isOpen={!!deleteConfirm}
          title="Подтверждение удаления"
          message={
            <>
              Вы уверены, что хотите удалить <strong>объект "{deleteConfirm?.name}"</strong>?
              {deleteConfirm?.children?.length > 0 && (
                <><br /><br />Все дочерние объекты также будут удалены.</>
              )}
            </>
          }
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />

      {/* Модальное окно выбора здания из справочника УМБ */}
      <BuildingSelectModal
        isOpen={buildingModalOpen}
        parentId={selectedParentId}
        onClose={() => {
          setBuildingModalOpen(false);
          setSelectedParentId(null);
        }}
      />

      {/* Модальное окно выбора аудитории из справочника */}
      <RoomSelectModal
        isOpen={roomModalOpen}
        parentId={selectedParentId}
        onClose={() => {
          setRoomModalOpen(false);
          setSelectedParentId(null);
        }}
      />

      {/* Модалка предупреждения о несохранённых изменениях */}
      {unsavedChangesConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>Несохранённые изменения</div>
            <div className={styles.modalContent}>
              <p className={styles.confirmText}>
                У вас есть несохранённые изменения. Что вы хотите сделать?
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setUnsavedChangesConfirm(null)}
              >
                Отмена
              </button>
              <button
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDiscardAndSwitch}
              >
                Отменить изменения
              </button>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleSaveAndSwitch}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  objectTree: selectObjectTree(state),
  objects: state.infoTerminal.objects,
  selectedObjectId: state.infoTerminal.selectedObjectId
});

export default connect(mapStateToProps, {
  setSelectedObject,
  addObject,
  setObjects,
  deleteObject
})(ObjectTree);
